import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Heading1, P, Stack, Margin, Heading3 } from '@ovotech/nebula';
import { PageWithSimpleHeader } from '@/src/components';
import {
  OVO_ENERGY_APP_STORE_URL,
  OVO_ENERGY_GOOGLE_PLAY_URL,
} from '@/src/constants/links';
import { ReactComponent as AppStoreBadge } from '@/src/resources/apple-store-badge.svg';
import { ReactComponent as PlayStoreBadge } from '@/src/resources/google-play-badge.svg';
import { useLocation } from 'react-router-dom-v5';

const StyledContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  padding: ${({ theme }) => theme.space[8]} 0;
`;

const StyledBadgeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space[4]};
  margin-top: ${({ theme }) => theme.space[8]};

  @media (min-width: 576px) {
    flex-direction: row;
    justify-content: center;
    gap: ${({ theme }) => theme.space[6]};
  }
`;

const StyledBadgeLink = styled.a`
  display: inline-block;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.8;
  }

  svg {
    height: 50px;
    width: auto;
  }
`;

/**
 * AppLandingPage serves as the user-facing fallback for the '/get-app' route.
 *
 * This page is rendered when:
 * 1. The Vercel Middleware (middleware.ts) detects a non-mobile device (Desktop).
 * 2. The mobile redirect fails or is bypassed.
 *
 */
export const AppLandingPage = () => {
  const { search } = useLocation();

  // Helper to append existing UTMs to store links
  const getStoreUrlWithParams = (baseUrl: string) => {
    if (!search) return baseUrl;
    const url = new URL(baseUrl);
    const params = new URLSearchParams(search);
    params.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
    return url.toString();
  };

  useEffect(() => {
    const userAgent = navigator.userAgent;
    let targetUrl: string | null = null;

    // iPadOS safari shows up as macintosh
    if (/android/i.test(userAgent)) {
      targetUrl = OVO_ENERGY_GOOGLE_PLAY_URL;
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      targetUrl = OVO_ENERGY_APP_STORE_URL;
    }

    if (targetUrl) {
      window.location.replace(getStoreUrlWithParams(targetUrl));
    }
  }, [search]);

  return (
    <PageWithSimpleHeader title="Get the OVO app">
      <StyledContainer>
        <Stack spaceBetween={4}>
          <Heading1>Get the OVO Energy app</Heading1>
          <P>
            Manage your energy account on the go. Track your usage, submit meter
            readings, and more.
          </P>

          <StyledBadgeContainer>
            <StyledBadgeLink
              href={getStoreUrlWithParams(OVO_ENERGY_APP_STORE_URL)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
            >
              <AppStoreBadge />
            </StyledBadgeLink>

            <StyledBadgeLink
              href={getStoreUrlWithParams(OVO_ENERGY_GOOGLE_PLAY_URL)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
            >
              <PlayStoreBadge />
            </StyledBadgeLink>
          </StyledBadgeContainer>

          <Margin top={8}>
            <Heading3>Already got the app?</Heading3>
            <P>Open it on your phone to pick up where you left off.</P>
          </Margin>
        </Stack>
      </StyledContainer>
    </PageWithSimpleHeader>
  );
};

export default AppLandingPage;
